import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Logo, Avatar, Icon } from '../components/components';

// Custom hook to detect window width dynamically for responsive layouts
export const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

export interface LayoutProps {
  children: React.ReactNode;
}

export interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  userRole?: string;
}

export const Navbar: React.FC<LayoutProps> = ({ children }) => {
  return (
    <nav
      style={{
        height: '64px',
        backgroundColor: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `0 ${theme.spacing.lg}`,
        boxShadow: theme.shadows.sm,
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.sticky,
        boxSizing: 'border-box'
      }}
    >
      <Logo size="sm" />
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        {children}
      </div>
    </nav>
  );
};

export const Header: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode }> = ({
  title,
  subtitle,
  action
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.border}`,
        marginBottom: theme.spacing.lg
      }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: theme.typography.fontSize.xxl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.textPrimary }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ margin: `${theme.spacing.xs} 0 0 0`, fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, userRole = 'OWNER' }) => {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'crm', label: 'CRM Leads', icon: '👥' },
    { id: 'students', label: 'Students', icon: '🎓' },
    { id: 'faculty', label: 'Faculty', icon: '🧑‍🏫' },
    { id: 'courses', label: 'Courses', icon: '📚' },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'builder', label: 'Website Builder', icon: '🌐' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
  ];

  return (
    <aside
      style={{
        width: '260px',
        backgroundColor: theme.colors.primary,
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.fixed,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ padding: theme.spacing.lg, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Logo theme="dark" size="sm" />
      </div>
      
      <div style={{ flex: 1, padding: theme.spacing.md, display: 'flex', flexDirection: 'column', gap: theme.spacing.xs, overflowY: 'auto' }}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.md,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                borderRadius: theme.radius.md,
                cursor: 'pointer',
                backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                fontWeight: isActive ? theme.typography.fontWeight.bold : theme.typography.fontWeight.normal,
                transition: theme.transitions.colors,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              <span>{tab.label}</span>
            </div>
          );
        })}
      </div>

      <div style={{ padding: theme.spacing.lg, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <Avatar name="Admin User" size="sm" />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold }}>Admin</span>
          <span style={{ fontSize: theme.typography.fontSize.xs, color: 'rgba(255,255,255,0.6)' }}>{userRole}</span>
        </div>
      </div>
    </aside>
  );
};

export const Footer: React.FC = () => {
  return (
    <footer
      style={{
        padding: `${theme.spacing.lg} 0`,
        borderTop: `1px solid ${theme.colors.border}`,
        marginTop: 'auto',
        textAlign: 'center',
        fontSize: theme.typography.fontSize.xs,
        color: theme.colors.textMuted
      }}
    >
      <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Millish Coaching OS. All rights reserved.</p>
      <p style={{ margin: `${theme.spacing.xs} 0 0 0` }}>SaaS Learning Management Platform | Certified Secure</p>
    </footer>
  );
};

export const PageContainer: React.FC<LayoutProps> = ({ children }) => {
  const width = useWindowWidth();
  const isMobile = width < 768;
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.typography.fontFamily.sans
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? theme.spacing.md : theme.spacing.lg,
          boxSizing: 'border-box',
          maxWidth: theme.spacing.containerMaxWidth,
          margin: '0 auto',
          width: '100%'
        }}
      >
        {children}
      </div>
    </div>
  );
};
